import {
    FormEvent,
    KeyboardEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Container } from "reactstrap";
import {
    faCheck,
    faCopy,
    faEye,
    faEyeSlash,
    faPencil,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import useFetch from "Hooks/useFetch";
import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";
import usePageTitle from "Hooks/usePageTitle";
import styles from "Pages/Setup/Configuration2/Configuration2.module.css";

/**
 * Configuration2 — preview of a port of the Portal2/HTMX configuration look-and-feel.
 * Lives at /setup/configuration2 alongside the existing /setup/configuration page.
 *
 * Reuses the existing API endpoints (no backend changes):
 *   GET  /api/configuration/{company_id}
 *   POST /api/configuration/new/{company_id}
 *   POST /api/configuration/edit/{company_id}
 *   POST /api/configuration/delete/{company_id}
 *
 * Layout vs the existing modal-based page:
 *   - List of rows; each row is editable inline (no modal).
 *   - "+ New setting" disclosure at the top.
 *   - Setting cell shows copy icon + key; value cell shows copy icon + value
 *     (masked with eye toggle when obscured).
 *   - Audit info (id, full key, created, modified, etc.) on row hover via title=.
 *   - Filter input supports comma-separated terms with `!` to exclude.
 *   - Type column rendered as a small pill when not "text".
 *   - Multi-line value editing via auto-grow textarea, Ctrl+Enter saves, Esc cancels.
 */

interface IConfigDto {
    id?: number;
    keyName: string;
    keyValue: string;
    company_id?: string;
    purpose?: string;
    data_type?: string;
    is_obscured?: boolean;
    created_date?: string;
    modified_date?: string;
}

const DATA_TYPES = ["text", "true/false", "number", "json", "secret"] as const;

/** Heuristic mask — same logic the original Portal Configuration page used.
 *  Even when is_obscured=false, mask values for keys whose name contains a
 *  sensitive token. Defense in depth — old config rows pre-date the flag. */
const HIDDEN_KEYNAME_HINTS = ["token", "password", "pwd", "secret", "connectionstring"];
function isHiddenByKey(keyName: string): boolean {
    const lower = (keyName || "").toLowerCase();
    return HIDDEN_KEYNAME_HINTS.some(h => lower.includes(h));
}

/** Mirrors the HTMX page's client-side validator (see
 *  WebApps/Portal2/docs/htmx-client-validation.md). Returns null if the value
 *  is acceptable for the given type, or a user-facing error string. */
function validateValue(type: string, value: string): string | null {
    if (!value) return null;
    switch ((type || "").toLowerCase()) {
        case "true/false":
            return /^(true|false|yes|no|1|0)$/i.test(value)
                ? null
                : `Type "true/false" only accepts: true, false, yes, no, 1, 0. Got: "${value}".`;
        case "number":
            return /^-?\d+(\.\d+)?$/.test(value.trim())
                ? null
                : `Type "number" requires a numeric value. Got: "${value}".`;
        case "json":
            try { JSON.parse(value); return null; }
            catch (e: any) { return `Type "json" requires valid JSON: ${e?.message ?? "parse error"}`; }
        default:
            return null;
    }
}

/** Substring filter with comma+! syntax (matches Portal2/Configuration.cshtml). */
function termTest(haystack: string, raw: string): boolean {
    const include: string[] = [];
    const exclude: string[] = [];
    for (const part of raw.split(",").map((s) => s.trim()).filter(Boolean)) {
        if (part.startsWith("!") && part.length > 1) {
            exclude.push(part.slice(1).toLowerCase());
        } else if (!part.startsWith("!")) {
            include.push(part.toLowerCase());
        }
    }
    const h = (haystack || "").toLowerCase();
    if (include.length > 0 && !include.some((t) => h.includes(t))) return false;
    for (const t of exclude) if (h.includes(t)) return false;
    return true;
}

const FILTER_TIP =
    "Filter visible rows. Comma-separate for OR. Prefix ! to exclude.\n\n" +
    "Examples:\n" +
    "  smtp           contains \"smtp\"\n" +
    "  smtp,jde       contains \"smtp\" OR \"jde\"\n" +
    "  !secret        does NOT contain \"secret\"\n" +
    "  smtp,!password contains \"smtp\" AND not \"password\"";

function fmtDate(s?: string): string {
    if (!s) return "—";
    // Backend sends ISO; trim to seconds.
    const t = s.replace("T", " ").split(".")[0];
    return t || s;
}

/** Auto-grow textarea by setting height to scrollHeight on input. */
function autoGrow(t: HTMLTextAreaElement) {
    t.style.height = "auto";
    t.style.height = t.scrollHeight + 2 + "px";
}

export default function Configuration2() {
    usePageTitle("Configuration");
    const [userContext] = useUserContext();
    const { get, post } = useFetch();
    const { newAlert } = useAlertContext();

    const [configs, setConfigs] = useState<IConfigDto[]>([]);
    const [filter, setFilter] = useState("");
    const [settingFilter, setSettingFilter] = useState("");
    const [valueFilter, setValueFilter] = useState("");
    const [editingId, setEditingId] = useState<number | "new" | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [revealedIds, setRevealedIds] = useState<Set<number>>(new Set());
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const ignore = useRef(false);

    const companyPrefix = useMemo(
        () => `${userContext.companyId}.`,
        [userContext.companyId]
    );

    const loadConfigs = useCallback(async () => {
        if (ignore.current) return;
        const resp = await get(`/api/configuration/${userContext.companyId}`);
        if (!resp.ok) {
            setError("Failed to load configuration");
            return;
        }
        const data: IConfigDto[] = await resp.json();
        if (ignore.current) return;
        setConfigs(data.sort((a, b) => a.keyName.localeCompare(b.keyName)));
        setError(null);
    }, [get, userContext.companyId]);

    useEffect(() => {
        ignore.current = false;
        loadConfigs();
        return () => {
            ignore.current = true;
        };
    }, [loadConfigs]);

    const stripPrefix = useCallback(
        (full: string): string =>
            full.toLowerCase().startsWith(companyPrefix.toLowerCase())
                ? full.substring(companyPrefix.length)
                : full,
        [companyPrefix]
    );

    const filteredConfigs = useMemo(() => {
        const g = filter.trim();
        const s = settingFilter.trim();
        const v = valueFilter.trim();
        if (!g && !s && !v) return configs;
        return configs.filter((c) => {
            const settingText =
                stripPrefix(c.keyName) +
                " " +
                (c.purpose || "") +
                " " +
                (c.data_type || "");
            const valueText = c.keyValue || "";
            if (g && !termTest(settingText + " " + valueText, g)) return false;
            if (s && !termTest(settingText, s)) return false;
            if (v && !termTest(valueText, v)) return false;
            return true;
        });
    }, [configs, filter, settingFilter, valueFilter, stripPrefix]);

    const copyToClipboard = useCallback(async (text: string, id: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedId(id);
            setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 800);
        } catch {
            // Silent — copy is best-effort.
        }
    }, []);

    const toggleReveal = useCallback((id: number) => {
        setRevealedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const submit = useCallback(
        async (input: ConfigFormInput, mode: "new" | "edit") => {
            const body: IConfigDto = {
                company_id: userContext.companyId,
                data_type: input.data_type || "text",
                is_obscured: !!input.is_obscured,
                purpose: input.purpose || "",
                keyName:
                    mode === "new"
                        ? `${userContext.companyId}.${input.key.trim()}`
                        : input.fullKey || "",
                keyValue: (input.value || "").trim(),
            };

            // Duplicate-key check on new
            if (mode === "new") {
                if (
                    configs.some(
                        (c) =>
                            c.keyName.toLowerCase() ===
                            body.keyName.toLowerCase()
                    )
                ) {
                    setError(`"${input.key.trim()}" already exists.`);
                    return false;
                }
            }

            const url = `/api/configuration/${mode}/${userContext.companyId}`;
            const resp = await post(url, body);
            if (!resp.ok) {
                let detail = "";
                try {
                    const err = await resp.json();
                    detail = err.detail || err.message || "";
                } catch {
                    /* swallow */
                }
                setError(detail || `Save failed (${resp.status})`);
                newAlert({
                    isOpen: true,
                    color: "danger",
                    message: detail || "Save failed",
                });
                return false;
            }

            setError(null);
            newAlert({
                isOpen: true,
                color: "success",
                message: "Saved",
            });
            await loadConfigs();
            return true;
        },
        [configs, userContext.companyId, post, loadConfigs, newAlert]
    );

    const onDelete = useCallback(
        async (cfg: IConfigDto) => {
            const shortKey = stripPrefix(cfg.keyName);
            // eslint-disable-next-line no-restricted-globals
            if (!confirm(`Delete ${shortKey}?`)) return;
            const url = `/api/configuration/delete/${userContext.companyId}`;
            const resp = await post(url, cfg);
            if (!resp.ok) {
                setError("Delete failed");
                newAlert({
                    isOpen: true,
                    color: "danger",
                    message: "Delete failed",
                });
                return;
            }
            await loadConfigs();
        },
        [stripPrefix, userContext.companyId, post, loadConfigs, newAlert]
    );

    return (
        <Container fluid className={styles.page}>
            <h1 className={styles.title}>Configuration</h1>

            {error && <div className={styles.bannerError}>{error}</div>}

            <div className={styles.container}>
                {/* + New setting disclosure */}
                <details
                    className={styles.new}
                    open={editingId === "new"}
                    onToggle={(e) =>
                        setEditingId(
                            (e.target as HTMLDetailsElement).open ? "new" : null
                        )
                    }
                >
                    <summary>+ New setting</summary>
                    {editingId === "new" && (
                        <ConfigEditForm
                            mode="new"
                            companyPrefix={companyPrefix}
                            onCancel={() => setEditingId(null)}
                            onSubmit={async (input) => {
                                const ok = await submit(input, "new");
                                if (ok) setEditingId(null);
                                return ok;
                            }}
                        />
                    )}
                </details>

                {/* Filter */}
                <div className={styles.filterRow}>
                    <input
                        type="search"
                        placeholder="Filter settings…"
                        title={FILTER_TIP}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        autoFocus
                    />
                </div>

                {/* Table */}
                <table className={styles.table}>
                    <colgroup>
                        <col style={{ width: "22rem" }} />
                        <col />
                        <col style={{ width: "7rem" }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Setting</th>
                            <th>Value</th>
                            <th></th>
                        </tr>
                        <tr className={styles.colFilterRow}>
                            <th>
                                <input
                                    type="search"
                                    placeholder="Filter setting…"
                                    value={settingFilter}
                                    onChange={(e) => setSettingFilter(e.target.value)}
                                />
                            </th>
                            <th>
                                <input
                                    type="search"
                                    placeholder="Filter value…"
                                    value={valueFilter}
                                    onChange={(e) => setValueFilter(e.target.value)}
                                />
                            </th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredConfigs.length === 0 && (
                            <tr>
                                <td colSpan={3} className={styles.empty}>
                                    {configs.length === 0
                                        ? `No settings yet for ${userContext.companyId}.`
                                        : "No matches."}
                                </td>
                            </tr>
                        )}
                        {filteredConfigs.map((cfg) =>
                            editingId === cfg.id ? (
                                <tr
                                    key={cfg.id}
                                    className={styles.editRowTr}
                                >
                                    <td colSpan={3}>
                                        <ConfigEditForm
                                            mode="edit"
                                            companyPrefix={companyPrefix}
                                            existing={cfg}
                                            onCancel={() => setEditingId(null)}
                                            onSubmit={async (input) => {
                                                const ok = await submit(
                                                    input,
                                                    "edit"
                                                );
                                                if (ok) setEditingId(null);
                                                return ok;
                                            }}
                                        />
                                    </td>
                                </tr>
                            ) : (
                                <DisplayRow
                                    key={cfg.id ?? cfg.keyName}
                                    cfg={cfg}
                                    shortKey={stripPrefix(cfg.keyName)}
                                    revealed={
                                        cfg.id != null &&
                                        revealedIds.has(cfg.id)
                                    }
                                    onToggleReveal={() =>
                                        cfg.id != null && toggleReveal(cfg.id)
                                    }
                                    onEdit={() =>
                                        cfg.id != null && setEditingId(cfg.id)
                                    }
                                    onDelete={() => onDelete(cfg)}
                                    onCopy={(text, id) =>
                                        copyToClipboard(text, id)
                                    }
                                    copiedId={copiedId}
                                />
                            )
                        )}
                    </tbody>
                </table>
            </div>
        </Container>
    );
}

interface ConfigFormInput {
    key: string;
    value: string;
    purpose: string;
    data_type: string;
    is_obscured: boolean;
    /** For edit mode — the full prefixed key (server expects it back unchanged). */
    fullKey?: string;
}

interface EditFormProps {
    mode: "new" | "edit";
    companyPrefix: string;
    existing?: IConfigDto;
    onCancel: () => void;
    onSubmit: (input: ConfigFormInput) => Promise<boolean>;
}

function ConfigEditForm({
    mode,
    companyPrefix,
    existing,
    onCancel,
    onSubmit,
}: EditFormProps) {
    const shortKey = useMemo(() => {
        if (!existing) return "";
        return existing.keyName
            .toLowerCase()
            .startsWith(companyPrefix.toLowerCase())
            ? existing.keyName.substring(companyPrefix.length)
            : existing.keyName;
    }, [existing, companyPrefix]);

    const [key, setKey] = useState(mode === "new" ? "" : shortKey);
    const [value, setValue] = useState(existing?.keyValue || "");
    const [purpose, setPurpose] = useState(existing?.purpose || "");
    const [dataType, setDataType] = useState(existing?.data_type || "text");
    const [obscured, setObscured] = useState(existing?.is_obscured || false);
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    const valueRef = useRef<HTMLTextAreaElement>(null);
    const keyRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Auto-grow + autofocus.
        if (valueRef.current) autoGrow(valueRef.current);
        if (mode === "new") keyRef.current?.focus();
        else valueRef.current?.focus();
    }, [mode]);

    const handleSubmit = useCallback(
        async (e?: FormEvent) => {
            if (e) e.preventDefault();
            if (submitting) return;
            const err = validateValue(dataType, value);
            if (err) {
                setValidationError(err);
                return;
            }
            setValidationError(null);
            setSubmitting(true);
            try {
                await onSubmit({
                    key: key.trim(),
                    value,
                    purpose,
                    data_type: dataType,
                    is_obscured: obscured,
                    fullKey: existing?.keyName,
                });
            } finally {
                setSubmitting(false);
            }
        },
        [submitting, onSubmit, key, value, purpose, dataType, obscured, existing]
    );

    const onValueKey = useCallback(
        (e: KeyboardEvent<HTMLTextAreaElement>) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
            } else if (e.key === "Escape") {
                e.preventDefault();
                onCancel();
            }
        },
        [handleSubmit, onCancel]
    );

    return (
        <form className={styles.editForm} onSubmit={handleSubmit}>
            <div className={styles.editKeyline}>
                <span className={styles.editKeylabel}>Key</span>
                {mode === "new" ? (
                    <>
                        <input
                            ref={keyRef}
                            type="text"
                            className={styles.keyInput}
                            placeholder="machineName"
                            value={key}
                            onChange={(e) => setKey(e.target.value)}
                            required
                            autoComplete="off"
                            minLength={1}
                            maxLength={100}
                        />
                        <span className={styles.keyHint}>
                            stored as{" "}
                            <code>
                                {companyPrefix}
                                <em>key</em>
                            </code>
                        </span>
                    </>
                ) : (
                    <span className={styles.key}>{shortKey}</span>
                )}
            </div>

            <textarea
                ref={valueRef}
                className={styles.valueProminent}
                value={value}
                onChange={(e) => {
                    setValue(e.target.value);
                    autoGrow(e.target as HTMLTextAreaElement);
                }}
                onKeyDown={onValueKey}
                rows={Math.max(
                    2,
                    Math.min(12, (value || "").split("\n").length + 1)
                )}
                placeholder="Value"
                autoComplete="off"
                maxLength={4096}
            />

            <div className={styles.editMeta}>
                <label>
                    <span className={styles.editMetalabel}>Purpose</span>
                    <input
                        type="text"
                        className={styles.metaInput}
                        value={purpose}
                        onChange={(e) => setPurpose(e.target.value)}
                        placeholder="optional"
                        autoComplete="off"
                    />
                </label>
                <label>
                    <span className={styles.editMetalabel}>Type</span>
                    <select
                        className={styles.metaInput}
                        value={dataType}
                        onChange={(e) => setDataType(e.target.value)}
                    >
                        {DATA_TYPES.map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </select>
                </label>
                <label className={styles.checkbox}>
                    <input
                        type="checkbox"
                        checked={obscured}
                        onChange={(e) => setObscured(e.target.checked)}
                    />
                    <span>Obscured</span>
                </label>
            </div>
            {validationError && (
                <div className={styles.editError} role="alert">
                    {validationError}
                </div>
            )}
            <span className={styles.editActions}>
                <button
                    type="submit"
                    className={styles.btnPrimary}
                    disabled={submitting}
                    title={mode === "new" ? "Add" : "Save (Ctrl+Enter)"}
                >
                    {mode === "new" ? "Add" : "Save"}
                </button>
                <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={onCancel}
                    title="Cancel (Esc)"
                >
                    Cancel
                </button>
            </span>
        </form>
    );
}

interface DisplayRowProps {
    cfg: IConfigDto;
    shortKey: string;
    revealed: boolean;
    onToggleReveal: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onCopy: (text: string, id: string) => void;
    copiedId: string | null;
}

function DisplayRow({
    cfg,
    shortKey,
    revealed,
    onToggleReveal,
    onEdit,
    onDelete,
    onCopy,
    copiedId,
}: DisplayRowProps) {
    const dataType = cfg.data_type && cfg.data_type !== "" ? cfg.data_type : "text";
    // Mask if the row is flagged OR the keyname looks sensitive (token, password, etc).
    const obscured = !!cfg.is_obscured || isHiddenByKey(cfg.keyName);
    const displayValue = obscured && !revealed ? "••••••" : cfg.keyValue;

    const tooltip =
        `ID:         ${cfg.id ?? "—"}\n` +
        `Company:    ${cfg.company_id ?? "—"}\n` +
        `Full key:   ${cfg.keyName}\n` +
        `Value:      ${obscured ? "(obscured)" : cfg.keyValue}\n` +
        `Purpose:    ${cfg.purpose || "—"}\n` +
        `Data type:  ${dataType}\n` +
        `Obscured:   ${obscured ? "yes" : "no"}\n` +
        `Created:    ${fmtDate(cfg.created_date)}\n` +
        `Modified:   ${fmtDate(cfg.modified_date)}`;

    const keyCopyId = `copy-key-${cfg.id}`;
    const valCopyId = `copy-val-${cfg.id}`;

    return (
        <tr title={tooltip} onDoubleClick={onEdit}>
            <td>
                <div className={styles.keyRow}>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.copy} ${
                            copiedId === keyCopyId ? styles.copied : ""
                        }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCopy(shortKey, keyCopyId);
                        }}
                        title="Copy key"
                    >
                        <FontAwesomeIcon
                            icon={copiedId === keyCopyId ? faCheck : faCopy}
                        />
                    </button>
                    <span className={styles.key}>{shortKey}</span>
                    {dataType !== "text" && (
                        <span className={styles.typePill}>{dataType}</span>
                    )}
                </div>
                {cfg.purpose && (
                    <div className={styles.purpose}>{cfg.purpose}</div>
                )}
            </td>
            <td>
                <div className={styles.valueRow}>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.copy} ${
                            copiedId === valCopyId ? styles.copied : ""
                        }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCopy(cfg.keyValue, valCopyId);
                        }}
                        title="Copy value"
                    >
                        <FontAwesomeIcon
                            icon={copiedId === valCopyId ? faCheck : faCopy}
                        />
                    </button>
                    <span
                        className={
                            obscured && !revealed
                                ? `${styles.value} ${styles.masked}`
                                : styles.value
                        }
                    >
                        {displayValue}
                    </span>
                    {obscured && (
                        <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleReveal();
                            }}
                            title={revealed ? "Hide" : "Show"}
                        >
                            <FontAwesomeIcon
                                icon={revealed ? faEyeSlash : faEye}
                            />
                        </button>
                    )}
                </div>
            </td>
            <td className={styles.actions}>
                <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                    }}
                    title="Edit"
                >
                    <FontAwesomeIcon icon={faPencil} />
                </button>
                <button
                    type="button"
                    className={`${styles.iconBtn} ${styles.danger}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    title="Delete"
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>
            </td>
        </tr>
    );
}
